import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import logo from "./assets/dialhoop_logo.png";
import wordmark from "./assets/dialhoop_name.png";

/* ── Card colour map ── */
const COLOR_MAP = {
  lavender: { bg: "#e1c2e9", text: "#5a2d72", light: "#f3e8f8" },
  mint: { bg: "#ccefef", text: "#1a6060", light: "#e8f8f8" },
  lime: { bg: "#def8c3", text: "#2d5a0d", light: "#f0fce0" },
  peach: { bg: "#ffd8bb", text: "#7a3a00", light: "#fff0e5" },
  yellow: { bg: "#ede796", text: "#5a4a00", light: "#fafad0" },
};
const DEFAULT_COLOR = { bg: "#e1c2e9", text: "#5a2d72", light: "#f3e8f8" };

const Brand = () => {
  return (
    <div style={styles.container}>
      <img src={logo} alt="DialHoop logo" style={styles.logo} />
      <img src={wordmark} alt="DialHoop" style={styles.wordmark} />
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer"
  },
  logo: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
  },
  wordmark: {
    height: 18,
    objectFit: "contain"
  }
};

/* ── Copy to clipboard ── */
async function copyText(text, setCopied) {
  try {
    await navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 1800);
  } catch {
    // fallback
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    setCopied(text);
    setTimeout(() => setCopied(null), 1800);
  }
}

/* ── Medical badge ── */
function MedBadge({ icon, label, value, accent }) {
  if (!value) return null;
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      background: "#fff", borderRadius: 14, padding: "14px 10px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.07)", flex: "1 1 0", minWidth: 80,
      border: `1.5px solid ${accent}22`,
    }}>
      <span style={{ fontSize: 22, marginBottom: 4 }}>{icon}</span>
      <span style={{ fontSize: 11, color: "#888", fontWeight: 500, letterSpacing: "0.3px", textTransform: "uppercase", marginBottom: 3 }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", textAlign: "center", lineHeight: 1.3 }}>{value}</span>
    </div>
  );
}

/* ── Contact card ── */
function ContactCard({ name, phone, index, copiedNum, setCopied, accent }) {
  if (!name && !phone) return null;
  const isCopied = copiedNum === phone;
  const colors = ["#e53935", "#1976d2", "#388e3c"];
  const color = colors[index % colors.length];

  return (
    <div style={{
      background: "#fff", borderRadius: 16, padding: "14px 16px",
      marginBottom: 10, boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
      border: `1px solid ${accent}33`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: "50%",
          background: `${color}18`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 16, flexShrink: 0,
        }}>
          {index === 0 ? "⭐" : "👤"}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1a1a", lineHeight: 1.2 }}>{name}</div>
          {phone && <div style={{ fontSize: 13, color: "#666", marginTop: 2 }}>{phone}</div>}
        </div>
      </div>
      {phone && (
        <div style={{ display: "flex", gap: 8 }}>
          <a href={`tel:${phone}`} style={{
            flex: 1, display: "flex", justifyContent: "center", alignItems: "center",
            height: 42, background: color, color: "#fff",
            borderRadius: 10, textDecoration: "none", fontWeight: 600, fontSize: 14, gap: 6,
          }}>
            📞 Call
          </a>
          {name !== "🚑 Ambulance" && (
            <a
              href={`https://wa.me/${phone.replace(/\D/g, "")}`}
              target="_blank"
              rel="noreferrer"
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 42,
                background: "#25D366",
                color: "#fff",
                borderRadius: 10,
                textDecoration: "none",
                fontWeight: 600,
                fontSize: 14,
                gap: 6,
              }}
            >
              💬 WhatsApp
            </a>
          )}
          <button onClick={() => copyText(phone, setCopied)} style={{
            width: 42, height: 42, borderRadius: 10,
            background: isCopied ? "#e8f5e9" : "#f5f5f5",
            border: isCopied ? "1px solid #81c784" : "1px solid #e0e0e0",
            cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            {isCopied ? "✓" : "📋"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Info row ── */
const InfoRow = ({ label, value }) => value ? (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid #f0f0f0" }}>
    <span style={{ fontSize: 13, color: "#888", fontWeight: 500 }}>{label}</span>
    <span style={{ fontSize: 13, color: "#1a1a1a", fontWeight: 600, textAlign: "right", maxWidth: "60%" }}>{value}</span>
  </div>
) : null;

/* ══ MAIN PROFILE PAGE ══════════════════════ */
export default function ProfilePage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copiedNum, setCopied] = useState(null);
  const [showMedicalInfo, setShowMedicalInfo] = useState(false);

  useEffect(() => {
    document.title = "DialHoop — Emergency Profile";
    getDoc(doc(db, "contacts", id))
      .then(snap => {
        if (snap.exists()) { setData(snap.data()); document.title = `${snap.data().name} — DialHoop`; }
        else setNotFound(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#f8f6fb", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
      <img src={logo} alt="DialHoop" style={{ width: 56, height: 56, borderRadius: "50%", animation: "spin 1.5s linear infinite" }} />
      <div style={{ fontSize: 14, color: "#888", fontFamily: "DM Sans,sans-serif" }}>Loading profile…</div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (notFound) return (
    <div style={{ minHeight: "100vh", background: "#f8f6fb", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
      <div style={{ fontFamily: "Syne,sans-serif", fontSize: 22, fontWeight: 700, color: "#1a1a1a", marginBottom: 8 }}>Profile not found</div>
      <div style={{ fontSize: 14, color: "#888", marginBottom: 24 }}>This QR code may be invalid or the profile was removed.</div>
      <a href="https://dialhoop.com" style={{ background: "#7C6FFF", color: "#fff", padding: "12px 24px", borderRadius: 100, fontWeight: 600, fontSize: 14, textDecoration: "none" }}>← DialHoop Home</a>
    </div>
  );

  const colorKey = data.cardColor || "lavender";
  const palette = COLOR_MAP[colorKey] || DEFAULT_COLOR;
  const contacts = (data.emergencyContacts || []).filter(c => c.name);

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: "#f4f2f8", minHeight: "100vh", paddingBottom: 48 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; }
        @keyframes fadeSlide { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* ── Hero banner ── */}
      <div style={{
        background: `linear-gradient(160deg, ${palette.bg} 0%, ${palette.light} 100%)`,
        paddingTop: "env(safe-area-inset-top, 0px)",
        position: "relative", overflow: "hidden",
      }}>
        {/* decorative circles */}
        <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: `${palette.bg}`, opacity: 0.5 }} />
        <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: `${palette.bg}`, opacity: 0.4 }} />

        {/* Nav */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px 0", position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Brand />
          </div>
          <div style={{ background: `${palette.text}18`, borderRadius: 100, padding: "4px 12px" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: palette.text, letterSpacing: "0.5px", textTransform: "uppercase" }}>Emergency Profile</span>
          </div>
        </div>

        {/* Profile identity */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "28px 20px 0", position: "relative", zIndex: 1 }}>
          {/* Avatar */}
          <div style={{
            width: 100, height: 100, borderRadius: "50%",
            border: `4px solid #fff`,
            boxShadow: `0 4px 20px ${palette.bg}aa`,
            overflow: "hidden", background: palette.bg, flexShrink: 0,
            animation: "fadeSlide .5s ease both",
          }}>
            {data.photo
              ? <img src={data.photo} alt={data.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Syne,sans-serif", fontSize: 36, fontWeight: 800, color: palette.text }}>
                {data.name.charAt(0).toUpperCase()}
              </div>
            }
          </div>

          {/* Name */}
          <div style={{ fontFamily: "Syne,sans-serif", fontSize: 26, fontWeight: 800, color: palette.text, marginTop: 14, textAlign: "center", lineHeight: 1.15, animation: "fadeSlide .5s .1s ease both", opacity: 0, animationFillMode: "forwards" }}>
            {data.name}
          </div>

          {/* Member ID badge */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: `${palette.text}14`, border: `1px solid ${palette.text}30`, borderRadius: 100, padding: "5px 14px", marginTop: 8, animation: "fadeSlide .5s .2s ease both", opacity: 0, animationFillMode: "forwards" }}>
            <span style={{ fontSize: 13 }}>🆔</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: palette.text, letterSpacing: "0.5px" }}>{data.memberId || "N/A"}</span>
          </div>

          {/* Quick info pills */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center", marginTop: 12, marginBottom: 32, animation: "fadeSlide .5s .3s ease both", opacity: 0, animationFillMode: "forwards" }}>
            {data.gender && (
              <span style={{ background: `${palette.text}14`, color: palette.text, borderRadius: 100, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
                {data.gender}
              </span>
            )}
            {data.dob && (
              <span style={{ background: `${palette.text}14`, color: palette.text, borderRadius: 100, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
                DOB: {data.dob}
              </span>
            )}
            {data.address && (
              <span style={{ background: `${palette.text}14`, color: palette.text, borderRadius: 100, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
                📍 {data.address}
              </span>
            )}
          </div>
        </div>

        {/* Wave bottom */}
        <svg viewBox="0 0 1440 48" style={{ display: "block", marginTop: -2, width: "100%" }} preserveAspectRatio="none">
          <path d="M0,48 L0,20 Q360,48 720,20 Q1080,-8 1440,20 L1440,48 Z" fill="#f4f2f8" />
        </svg>
      </div>

      {/* ── Content ── */}
      <div style={{ maxWidth: 480, margin: "0 auto", padding: "4px 16px 0" }}>

        {/* 🚨 Emergency alert banner */}
        {contacts.length > 0 && (
          <div style={{
            background: "linear-gradient(135deg,#ff5252,#e53935)",
            borderRadius: 16, padding: "14px 18px",
            marginBottom: 20, marginTop: 4,
            display: "flex", alignItems: "center", gap: 12,
            boxShadow: "0 4px 20px rgba(229,57,53,0.25)",
            animation: "fadeSlide .5s .35s ease both", opacity: 0, animationFillMode: "forwards",
          }}>
            <div style={{ fontSize: 28, flexShrink: 0 }}>🚨</div>
            <div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 14, marginBottom: 2 }}>Emergency Profile</div>
              <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 12, lineHeight: 1.5 }}>
                Scan to call emergency contacts. Medical info below.
              </div>
            </div>
          </div>
        )}

        {/* 🩺 Medical cards */}
        {/* 🩺 Medical cards */}
        <div
          style={{
            marginBottom: 20,
            animation: "fadeSlide .5s .4s ease both",
            opacity: 0,
            animationFillMode: "forwards",
            position: "relative",
          }}
        >
          <div
            onClick={() => {
              if (!showMedicalInfo) {
                const confirmed = window.confirm(
                  "⚠️ Medical Disclaimer\n\nThe medical information shown here is user-provided. Dialhoop is not responsible for the accuracy, authenticity, or misuse of this information."
                );

                if (confirmed) {
                  setShowMedicalInfo(true);
                }
              } else {
                setShowMedicalInfo(false);
              }
            }}
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 12,
              padding: "14px 16px",
              borderRadius: 20,
              cursor: "pointer",
              overflow: "hidden",

              /* Modern glass blur */
              background: "rgba(255,255,255,0.18)",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              border: "1px solid rgba(255,255,255,0.25)",

              /* Premium shadow */
              boxShadow:
                "0 8px 32px rgba(31,38,135,0.12), inset 0 1px 0 rgba(255,255,255,0.25)",

              transition: "all .3s ease",
            }}
          >
            {/* glow */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.02))",
                pointerEvents: "none",
              }}
            />

            <span style={{ fontSize: 18, zIndex: 1 }}>🩺</span>

            <span
              style={{
                fontFamily: "Syne,sans-serif",
                fontSize: 16,
                fontWeight: 700,
                color: "#1a1a1a",
                zIndex: 1,
              }}
            >
              Medical Info
            </span>

            <span
              style={{
                marginLeft: "auto",
                fontSize: 12,
                fontWeight: 600,
                color: "#666",
                zIndex: 1,
              }}
            >
              {showMedicalInfo ? "Hide" : "View"}
            </span>
          </div>

          {/* Hidden until warning accepted */}
          {showMedicalInfo && (
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                animation: "fadeSlide .35s ease",
              }}
            >
              <MedBadge
                icon="🩸"
                label="Blood Group"
                value={data.bloodGroup}
                accent={palette.bg}
              />

              <MedBadge
                icon="💉"
                label="Diabetes"
                value={data.diabetes}
                accent={palette.bg}
              />

              <MedBadge
                icon="❤️"
                label="Blood Pressure"
                value={data.bloodPressure}
                accent={palette.bg}
              />
            </div>
          )}
        </div>

        {/* Personal details */}
        {(data.gender || data.dob || data.address) && (
          <div style={{ background: "#fff", borderRadius: 16, padding: "4px 16px 4px", marginBottom: 20, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", animation: "fadeSlide .5s .45s ease both", opacity: 0, animationFillMode: "forwards" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 0 4px" }}>
              <span style={{ fontSize: 16 }}>👤</span>
              <span style={{ fontFamily: "Syne,sans-serif", fontSize: 14, fontWeight: 700, color: "#1a1a1a" }}>Personal Details</span>
            </div>
            <InfoRow label="Gender" value={data.gender} />
            <InfoRow label="Date of Birth" value={data.dob} />
            <InfoRow label="Address" value={data.address} />
          </div>
        )}

        {/* Emergency contacts */}
        {contacts.length > 0 && (
          <div style={{ animation: "fadeSlide .5s .5s ease both", opacity: 0, animationFillMode: "forwards" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 18 }}>📞</span>
              <span style={{ fontFamily: "Syne,sans-serif", fontSize: 16, fontWeight: 700, color: "#1a1a1a" }}>Emergency Contacts</span>
            </div>
            {contacts.map((c, i) => (
              <ContactCard key={i} name={c.name} phone={c.phone} index={i} copiedNum={copiedNum} setCopied={setCopied} accent={palette.bg} />
            ))}
            <ContactCard
              key="ambulance"
              name="🚑 Ambulance"
              phone="108"
              index={-1}
              copiedNum={copiedNum}
              setCopied={setCopied}
              accent={palette.bg}
            />
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 32, textAlign: "center", animation: "fadeSlide .5s .6s ease both", opacity: 0, animationFillMode: "forwards" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 6 }}>
            <Brand /> </div>
          <div style={{ fontSize: 11, color: "#aaa", lineHeight: 1.6, maxWidth: 300, margin: "0 auto" }}>
            For informational use only. Not a substitute for professional medical advice.
          </div>
          <a href="https://dialhoop.com" style={{ display: "inline-block", marginTop: 12, background: `linear-gradient(135deg,#7C6FFF,#FF6B9D)`, color: "#fff", padding: "10px 22px", borderRadius: 100, fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
            Get your own card →
          </a>
        </div>
      </div>
    </div>
  );
}
